import "./styles.css"
import { desktopRelease, links } from "./config"

type PromoPhase = "prerelease" | "launch" | "support"

interface PromoState {
  phase: PromoPhase
  title: string
  lead: string
  copy: string
  cta: string
  url: string
  artClass: "guardians-art" | "studio-art"
}

const GUARDIANS_RELEASE = new Date("2026-10-15T00:00:00")
const SUPPORT_SWITCH = new Date("2026-12-01T00:00:00")

function promoFor(now = new Date()): PromoState {
  if (now < GUARDIANS_RELEASE) {
    return {
      phase: "prerelease",
      title: "The Guardians",
      lead: "Releases October 15.",
      copy: "Cinematic carrier-based aerial combat from Neutron Studios, the developer of iMusicRecovery.",
      cta: "Wishlist on Steam →",
      url: links.guardians,
      artClass: "guardians-art",
    }
  }
  if (now < SUPPORT_SWITCH) {
    return {
      phase: "launch",
      title: "The Guardians",
      lead: "Available now on Steam.",
      copy: "If iMusicRecovery helped save your old library, checking out our newest game directly supports the developer.",
      cta: "View The Guardians →",
      url: links.guardians,
      artClass: "guardians-art",
    }
  }
  return {
    phase: "support",
    title: "Support Neutron Studios",
    lead: "iMusicRecovery is free.",
    copy: "If it helped you, support continued free tools and indie development by checking out Neutron Studios on Steam.",
    cta: "Support the developer on Steam →",
    url: links.neutronSteam,
    artClass: "studio-art",
  }
}

const app = document.querySelector<HTMLDivElement>("#app")!
const promo = promoFor()
const releaseReady = Boolean(desktopRelease.downloadUrl)

app.innerHTML = `
  <header class="topbar">
    <a class="brand" href="./" aria-label="iMusicRecovery home"><span class="brand-mark">♫</span><span class="brand-stack"><span>iMusicRecovery</span><span class="studio-credit">a free recovery utility from Neutron Studios</span></span></a>
    <nav class="topnav" aria-label="Main navigation">
      <a href="#download">Download</a>
      <a href="#compatibility">Compatibility</a>
      <a href="#safety">Safety</a>
      <a href="./privacy.html">Privacy</a>
    </nav>
  </header>

  <main class="page-shell">
    <section class="hero">
      <div class="hero-copy-wrap">
        <p class="eyebrow">FREE WINDOWS IPOD RECOVERY</p>
        <h1>Recover music and video from an old iPod without syncing or restoring it.</h1>
        <p class="hero-copy">iMusicRecovery is a Windows desktop application. It uses the normal Apple device stack, creates a RAW preservation copy first, verifies recovered files with SHA-256, and reconstructs an easy-to-browse music and video library.</p>
        <div class="hero-actions">
          <a class="primary-link ${releaseReady ? "" : "disabled"}" ${releaseReady ? `href="${desktopRelease.downloadUrl}"` : "aria-disabled=\"true\""} id="downloadTop">${releaseReady ? "Download for Windows" : "Windows build in hardware validation"}</a>
          <a class="secondary-link" href="#how-it-works">How it works</a>
        </div>
        <p class="hero-note">Windows 10/11 x64 · No account · No subscription · Recovered media stays on your computer</p>
      </div>
      <aside class="download-card" id="download">
        <span class="platform-badge">WINDOWS DESKTOP</span>
        <h2>iMusicRecovery ${desktopRelease.version}</h2>
        <p>The browser-only experiment has been retired as the primary Windows recovery path.</p>
        <div class="download-status ${releaseReady ? "ready" : "testing"}">
          <span class="status-dot"></span>
          <div><strong>${releaseReady ? "Public build available" : "Hardware validation in progress"}</strong><span>${releaseReady ? "Download the current Windows release." : "The download will be enabled after the first physical iPod validation."}</span></div>
        </div>
        ${releaseReady ? `<a class="download-button" href="${desktopRelease.downloadUrl}">Download Windows build ↓</a>` : `<button class="download-button" disabled>Download coming after validation</button>`}
        <small>Apple Mobile Device Support may be required. The app will tell you if it is missing.</small>
      </aside>
    </section>

    <section class="trust-row" aria-label="Product principles">
      <div><strong>Local recovery</strong><span>Music and video are recovered directly to storage you choose.</span></div>
      <div><strong>Preservation first</strong><span>RAW files are created before organized copies.</span></div>
      <div><strong>Verified copies</strong><span>SHA-256 is used to verify recovered RAW files.</span></div>
      <div><strong>No custom USB driver</strong><span>Uses the normal Apple Windows device stack.</span></div>
    </section>

    <section class="house-promo" aria-label="From Neutron Studios">
      <div class="house-art ${promo.artClass}" role="img" aria-label="Neutron Studios promotional artwork"></div>
      <div class="house-promo-copy">
        <p class="house-label">FROM NEUTRON STUDIOS</p>
        <h3>${promo.title}</h3>
        <p><span class="release">${promo.lead}</span> ${promo.copy}</p>
      </div>
      <a class="house-cta" href="${promo.url}" target="_blank" rel="noopener">${promo.cta}</a>
    </section>

    <section class="section-grid" id="how-it-works">
      <div class="section-heading">
        <p class="eyebrow">HOW IT WORKS</p>
        <h2>Simplified and Free</h2>
      </div>
      <div class="steps">
        <article><span>1</span><div><h3>Install and connect</h3><p>Launch iMusicRecovery and connect the iPod with its normal USB cable.</p></div></article>
        <article><span>2</span><div><h3>Scan the device</h3><p>The app identifies the compatible iPod and inventories Music and Purchases without copying yet.</p></div></article>
        <article><span>3</span><div><h3>Choose a folder</h3><p>Select an empty recovery folder and choose RAW-only or RAW + Organized recovery.</p></div></article>
        <article><span>4</span><div><h3>Recover and verify</h3><p>Files are read from the iPod, preserved as RAW, SHA-256 verified, then optionally organized by metadata.</p></div></article>
      </div>
    </section>

    <section class="split-section" id="safety">
      <article class="info-card">
        <p class="eyebrow compact">RECOVERY SAFETY</p>
        <h2>Designed around read-only device access.</h2>
        <p>The native recovery helper is intentionally built without AFC write, delete, rename, truncate, or directory-creation operations on the iPod. Pairing may create or validate the normal Apple host trust record.</p>
        <ul>
          <li>RAW preservation before library reconstruction</li>
          <li>SHA-256 verification of recovered device reads</li>
          <li>Resume-aware recovery manifests</li>
          <li>No sync, restore, firmware update, or erase workflow</li>
        </ul>
      </article>
      <article class="info-card" id="compatibility">
        <p class="eyebrow compact">COMPATIBILITY</p>
        <h2>Initial target: iPod touch 1st–7th generation.</h2>
        <p>The first release targets iOS-era iPod touch models through Apple Mobile Device Support. Traditional iPod classic, nano, mini, photo/video and shuffle models use a separate Disk Mode architecture and are planned separately.</p>
        <a class="text-link" href="${links.appleWindows}" target="_blank" rel="noopener">Apple Devices for Windows ↗</a>
      </article>
    </section>

    <section class="why-desktop">
      <div>
        <p class="eyebrow compact">WHY A DESKTOP APP?</p>
        <h2>Windows would let the browser see the iPod, but not reliably take control of Apple's USB interface.</h2>
      </div>
      <p>The desktop implementation uses Windows' existing Apple-device path instead of asking users to replace the driver with WinUSB. That makes the recovery experience closer to a normal consumer utility such as other iPhone/iPod management applications.</p>
    </section>

    <section class="games-section">
      <div class="section-heading small-heading">
        <p class="house-label">MORE FROM NEUTRON STUDIOS</p>
        <h2>Free tool, independent studio.</h2>
        <p>iMusicRecovery does not use third-party advertising. The only promotion here is for the studio that builds and maintains the tool.</p>
      </div>
      <div class="game-grid">
        <a class="game-card" href="${links.corporeal}" target="_blank" rel="noopener"><div class="game-art corporeal-art"></div><div><strong>CORPOREAL</strong><span>Paranormal investigation horror · Available on Steam</span></div></a>
        <a class="game-card" href="${links.guardians}" target="_blank" rel="noopener"><div class="game-art guardians-art"></div><div><strong>The Guardians</strong><span>${new Date() < GUARDIANS_RELEASE ? "Releases October 15, 2026" : "Available on Steam"}</span></div></a>
      </div>
    </section>
  </main>

  <footer>
    <div><strong>iMusicRecovery</strong><span>Free preservation-first recovery from Neutron Studios.</span></div>
    <div class="footer-links"><a href="./privacy.html">Privacy</a><a href="./terms.html">Terms</a><a href="${links.neutronSite}" target="_blank" rel="noopener">Neutron Studios</a><a href="${links.neutronSteam}" target="_blank" rel="noopener">Steam</a></div>
  </footer>
`

if (!releaseReady) {
  document.getElementById("downloadTop")?.addEventListener("click", (event) => event.preventDefault())
}

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("./sw.js", { updateViaCache: "none" }).then((registration) => registration.update()).catch(() => undefined)
  })
}

document.documentElement.dataset.appReady = "true"
